import { isCreatorParticipant, type Participant } from '@fluenceur/types';
import { useCallback } from 'react';
import { platformOf } from '@/components/contact/platform-of';
import { useOpenAffiliateSurface } from '@/hooks/use-open-affiliate-surface';
import { useOpenDiscoveredCreatorSurface } from '@/hooks/use-open-discovered-creator-surface';

type ProfileTarget =
  | { surface: 'affiliate'; userId: string }
  | { surface: 'discoveredCreator'; username: string };

/**
 * Which profile surface a participant leads to, or none at all. A creator known
 * only by their identity, with no affiliate account and no social handle, has
 * no page to route to.
 *
 * The rule lives here once: the button's enabled state and the click both read
 * this, so a participant can never be offered a click that goes nowhere.
 */
const resolveProfileTarget = (
  participant: Participant | null | undefined
): ProfileTarget | null => {
  if (!participant) return null;

  if (isCreatorParticipant(participant)) {
    if (participant.isAffiliate && participant.affiliateUserId) {
      return { surface: 'affiliate', userId: participant.affiliateUserId };
    }
    if (participant.socialProfileId && participant.socialProfile?.platformUsername) {
      return {
        surface: 'discoveredCreator',
        username: participant.socialProfile.platformUsername,
      };
    }
    return null;
  }

  return participant.userId ? { surface: 'affiliate', userId: participant.userId } : null;
};

export const canOpenParticipantProfile = (
  participant: Participant | null | undefined
): boolean => resolveProfileTarget(participant) !== null;

/**
 * Routes a conversation participant's "See profile" click to the right surface:
 *
 * - Affiliates (pure affiliates and creators who became affiliates) go through
 *   `useOpenAffiliateSurface`.
 * - Non-affiliate creators go through `useOpenDiscoveredCreatorSurface`, which
 *   resolves creators by platform username.
 *
 * A participant with no surface is a no-op.
 *
 * Returns a stable callback so the header can pass it straight to `onClick`.
 */
export const useOpenParticipantProfile = (participant: Participant | null | undefined) => {
  const openAffiliateSurface = useOpenAffiliateSurface();
  const openDiscoveredCreatorSurface = useOpenDiscoveredCreatorSurface();

  return useCallback(() => {
    const target = resolveProfileTarget(participant);
    if (!target) return;

    const platform = platformOf(participant?.socialProfile?.platform);

    if (target.surface === 'affiliate') {
      openAffiliateSurface(target.userId, { platform });
      return;
    }
    openDiscoveredCreatorSurface({ username: target.username }, platform);
  }, [participant, openAffiliateSurface, openDiscoveredCreatorSurface]);
};
